// Upload templates — the optional "document to download" card in the template
// editor (Owner Recapitalization Loan spec, surface U6a; every Upload template).
//
// One document per template. Attach / Replace upload the file at once
// (POST /collection/templates/form-attachment) and the editor saves it onto
// the NEW template version with the rest of the form; Remove marks it for
// removal on save. Each change saves a new version, so requests already sent
// keep the document of the version they were issued from (that is what the
// remove confirmation tells the admin). The file is never linked by URL:
// "Download" streams it through GET /collection/templates/{id}/form-attachment.

import { useRef, useState } from "react";
import { Download, FileText } from "lucide-react";
import { Button, Card, CardContent, Input, humanSize } from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { useDateFormatter } from "@/utils/datetime";
import {
  FORM_ATTACHMENT_ACCEPT,
  FORM_ATTACHMENT_DEFAULT_BUTTON_LABEL,
  FORM_ATTACHMENT_MAX_BYTES,
  collectionTemplatesApi,
  type FormAttachmentDescriptor,
} from "@/api/collectionTemplates";

/** The saved version's document as the editor shows it. */
export interface SavedFormAttachment {
  templateId: string;
  fileName: string;
  sizeBytes: number | null;
  uploadedAt: string | null;
  uploadedByName: string | null;
}

export const REMOVE_FORM_ATTACHMENT_CONFIRMATION =
  "Remove the attached document? New requests for this template will no longer include a download button. Requests already sent keep their document.";

interface TemplateFormAttachmentCardProps {
  /** The saved version's document, unless removed in this session. */
  saved: SavedFormAttachment | null;
  /** A document uploaded in this session, attached on save. */
  pending: FormAttachmentDescriptor | null;
  buttonLabel: string;
  onUploaded: (descriptor: FormAttachmentDescriptor) => void;
  onRemove: () => void;
  onButtonLabelChange: (value: string) => void;
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function TemplateFormAttachmentCard({
  saved,
  pending,
  buttonLabel,
  onUploaded,
  onRemove,
  onButtonLabelChange,
}: TemplateFormAttachmentCardProps) {
  const { toast } = useToast();
  const { date } = useDateFormatter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function handleFile(file: File | null) {
    if (!file) return;
    if (file.size > FORM_ATTACHMENT_MAX_BYTES) {
      toast({ tone: "error", message: "The document to download can be at most 50 MB." });
      return;
    }
    setBusy(true);
    try {
      const result = await collectionTemplatesApi.uploadFormAttachment(file);
      onUploaded(result);
    } catch (e) {
      toast({ tone: "error", message: e instanceof Error ? e.message : "Upload failed." });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function handleDownload(s: SavedFormAttachment) {
    try {
      saveBlob(await collectionTemplatesApi.downloadFormAttachment(s.templateId), s.fileName);
    } catch (e) {
      toast({ tone: "error", message: e instanceof Error ? e.message : "Download failed." });
    }
  }

  const shown = pending
    ? { fileName: pending.originalFileName, sizeBytes: pending.sizeBytes as number | null }
    : saved
      ? { fileName: saved.fileName, sizeBytes: saved.sizeBytes }
      : null;
  const detail = pending
    ? "Uploaded now · saved with this version"
    : saved
      ? [
          saved.uploadedAt ? `Uploaded ${date(saved.uploadedAt)}` : null,
          saved.uploadedByName ? `by ${saved.uploadedByName}` : null,
        ]
          .filter(Boolean)
          .join(" ")
      : "";

  return (
    <Card>
      <CardContent className="flex flex-col gap-space-3 p-space-4" data-testid="form-attachment-card">
        <h2 className="text-body font-display text-onyx m-0">Document to download (optional)</h2>
        <p className="text-small text-gray-mid m-0">
          Attach a form the recipient downloads, completes or signs, and uploads back. Leave empty
          for a plain upload.
        </p>

        <input
          ref={inputRef}
          type="file"
          accept={FORM_ATTACHMENT_ACCEPT}
          className="hidden"
          aria-label="Choose a document to download"
          data-testid="form-attachment-input"
          disabled={busy}
          onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
        />

        {shown ? (
          <div
            className="flex flex-wrap items-center justify-between gap-space-3 p-space-3 border border-border-light rounded-md bg-white-pure"
            data-testid="form-attachment-row"
          >
            <div className="flex items-center gap-space-2 min-w-0">
              <FileText size={18} className="text-gray-mid shrink-0" aria-hidden />
              <div className="min-w-0">
                <p className="text-small font-medium text-onyx m-0 break-words">{shown.fileName}</p>
                <p className="text-caption text-gray-mid m-0">
                  {[shown.sizeBytes != null ? humanSize(shown.sizeBytes) : null, detail || null]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-space-2">
              {saved && !pending && (
                <Button variant="secondary" type="button" onClick={() => void handleDownload(saved)}>
                  <Download size={14} className="mr-space-1" aria-hidden /> Download
                </Button>
              )}
              <Button
                variant="secondary"
                type="button"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
              >
                Replace
              </Button>
              <Button
                variant="secondary"
                type="button"
                disabled={busy}
                onClick={() => setConfirmRemove(true)}
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              variant="secondary"
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              Attach document
            </Button>
          </div>
        )}
        {busy && <p className="text-caption text-gray-mid m-0">Uploading…</p>}
        <p className="text-caption text-gray-mid m-0">
          PDF, Word (.doc, .docx) or Excel (.xls, .xlsx), up to 50 MB.
        </p>

        {shown && (
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>Button label</span>
            <Input
              value={buttonLabel}
              maxLength={100}
              placeholder={FORM_ATTACHMENT_DEFAULT_BUTTON_LABEL}
              onChange={(e) => onButtonLabelChange(e.target.value)}
            />
            <span className="text-caption text-gray-mid">
              Leave blank for “{FORM_ATTACHMENT_DEFAULT_BUTTON_LABEL}”.
            </span>
          </label>
        )}
      </CardContent>

      <ConfirmModal
        open={confirmRemove}
        title="Remove document"
        message={REMOVE_FORM_ATTACHMENT_CONFIRMATION}
        confirmLabel="Remove"
        destructive
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() => {
          setConfirmRemove(false);
          onRemove();
        }}
      />
    </Card>
  );
}
